import { useMemo, useState } from 'react';

/**
 * Utility functions for table sorting
 */

/**
 * Sort rows based on sortConfig and columns configuration
 * @param {Array} rows - The rows to sort
 * @param {Object} sortConfig - The sort configuration {key, direction}
 * @param {Array} columns - The columns configuration
 * @returns {Array} - The sorted rows
 */
export const sortRows = (rows, sortConfig, columns) => {
  if (!sortConfig.key || !rows.length) return rows;
  
  return [...rows].sort((a, b) => {
    // Find the column to get the value (using valueGetter if available)
    const column = columns.find(col => col.key === sortConfig.key);
    if (!column) return 0;
    
    // Get cell values
    const valueA = column.valueGetter ? column.valueGetter(a) : a[sortConfig.key];
    const valueB = column.valueGetter ? column.valueGetter(b) : b[sortConfig.key];
    
    // Handle empty values
    if (valueA === undefined || valueA === null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valueB === undefined || valueB === null) return sortConfig.direction === 'asc' ? 1 : -1;
    
    // Sort based on data type
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
    } else {
      return sortConfig.direction === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    }
  });
};

/**
 * Create a hook for table sorting
 * @param {Array} initialRows - The initial rows
 * @param {Array} columns - The columns configuration
 * @returns {Object} - The sorting hook with sortedRows and handleSort
 */
export const useTableSort = (initialRows, columns) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };
  
  const sortedRows = useMemo(() => {
    return sortRows(initialRows, sortConfig, columns);
  }, [initialRows, sortConfig, columns]);
  
  return { sortedRows, handleSort, sortConfig };
};